import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '../lib/supabase';

export function usePermissions() {
  const { profile } = useAuth();
  const [clientAccess, setClientAccess] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = profile?.role === 'admin';
  const isCliente = profile?.role === 'cliente';
  const isColaborador = profile?.role === 'colaborador';

  useEffect(() => {
    if (!profile) {
      setLoading(false);
      return;
    }

    const fetchAccess = async () => {
      setLoading(true);

      if (isAdmin) {
        setClientAccess([]);
        setPermissions([]);
        setLoading(false);
        return;
      }

      if (isCliente) {
        const { data } = await supabase
          .from('agency_cliente_users')
          .select('client_id')
          .eq('user_id', profile.id);
        setClientAccess((data || []).map(r => r.client_id));
      }

      if (isColaborador) {
        const [{ data: clients }, { data: perms }] = await Promise.all([
          supabase
            .from('agency_colaborador_clients')
            .select('client_id')
            .eq('colaborador_id', profile.id),
          supabase
            .from('agency_colaborador_permissions')
            .select('*')
            .eq('colaborador_id', profile.id),
        ]);
        setClientAccess((clients || []).map(r => r.client_id));
        setPermissions(perms || []);
      }

      setLoading(false);
    };

    fetchAccess();
  }, [profile, isAdmin, isCliente, isColaborador]);

  const canAccessClient = useCallback((clientId) => {
    if (!profile) return false;
    if (isAdmin) return true;
    return clientAccess.includes(clientId);
  }, [profile, isAdmin, clientAccess]);

  const hasPermission = useCallback((clientId, feature, level) => {
    if (!profile) return false;
    if (isAdmin) return true;
    if (isCliente) {
      if (!clientAccess.includes(clientId)) return false;
      // Clientes podem ver tudo dos seus clientes, mas nao editar/deletar o cliente
      if (feature === 'client_settings') return level === 'view';
      if (feature === 'approvals') return true;
      return level === 'view';
    }
    if (isColaborador) {
      const perm = permissions.find(
        p => p.client_id === clientId && p.feature === feature
      );
      if (!perm) return false;
      // permission hierarchy: approve > edit > view
      if (level === 'view') return true;
      if (level === 'edit') return perm.permission === 'edit' || perm.permission === 'approve';
      if (level === 'approve') return perm.permission === 'approve';
      return false;
    }
    return false;
  }, [profile, isAdmin, isCliente, isColaborador, clientAccess, permissions]);

  return {
    isAdmin,
    isCliente,
    isColaborador,
    canAccessClient,
    hasPermission,
    loading,
  };
}
